import React from "react";
import { motion } from "framer-motion";
import { Mail, MessageSquare, ArrowRight, ShieldCheck, Sparkles, Building2, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { openAgent } from "../utils/openAgent";

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-[#010710] text-white flex flex-col justify-between selection:bg-[#00D2EE]/30 selection:text-white">
      <Navbar />

      <main className="relative pt-32 pb-24 px-6 lg:px-[70px] max-w-[1440px] mx-auto w-full flex-1">
        {/* Ambient Glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-gradient-to-r from-[#00D2EE]/15 to-[#2B7FFF]/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto text-center mb-16">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#00D2EE]/10 border border-[#00D2EE]/30 text-[#00D2EE] text-[13px] font-semibold uppercase tracking-wider mb-6">
            <Sparkles className="w-4 h-4" />
            Connect With Simpel
          </span>
          <h1 className="text-[36px] sm:text-[46px] lg:text-[54px] font-extrabold tracking-tight leading-[1.15] text-white mb-6">
            Let’s Build Your Enterprise{" "}
            <span className="bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
              Cognitive Advantage.
            </span>
          </h1>
          <p className="text-[16px] sm:text-[18px] text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">
            Whether you are evaluating specialized AI copilots, automating legacy workflows, or deploying autonomous digital workers, our team is ready to assist.
          </p>
        </div>

        {/* 2-Column Contact Cards */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
          {/* Card 1: Interactive AI Consultation */}
          <div className="rounded-[20px] bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-white/10 p-8 flex flex-col justify-between shadow-[0_8px_32px_rgba(0,0,0,0.4)] backdrop-blur-md">
            <div>
              <div className="w-14 h-14 rounded-full bg-[#00D2EE]/15 border border-[#00D2EE]/30 flex items-center justify-center text-[#00D2EE] mb-6">
                <MessageSquare className="w-7 h-7" />
              </div>
              <h2 className="text-[22px] font-bold text-white mb-3">Instant AI Consultation</h2>
              <p className="text-[14px] leading-relaxed text-[#94A3B8] mb-6">
                Talk directly with Simmi, Simpel’s cognitive enterprise guide. Get immediate answers about solutions, architecture, and pilot timelines.
              </p>
            </div>
            <button
              onClick={openAgent}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#00D2EE] hover:bg-[#00D2EE]/90 text-[#010710] font-bold py-3.5 px-6 shadow-[0_0_24px_rgba(0,210,238,0.35)] transition-all duration-200 cursor-pointer"
            >
              <span>Launch Live Assistant</span>
              <ArrowRight className="w-4 h-4 stroke-[2.5]" />
            </button>
          </div>

          {/* Card 2: Enterprise Advisory & Inquiries */}
          <div className="rounded-[20px] bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-white/10 p-8 flex flex-col justify-between shadow-[0_8px_32px_rgba(0,0,0,0.4)] backdrop-blur-md">
            <div>
              <div className="w-14 h-14 rounded-full bg-[#2B7FFF]/15 border border-[#2B7FFF]/30 flex items-center justify-center text-[#2B7FFF] mb-6">
                <Mail className="w-7 h-7" />
              </div>
              <h2 className="text-[22px] font-bold text-white mb-3">Executive & Direct Inquiries</h2>
              <p className="text-[14px] leading-relaxed text-[#94A3B8] mb-6">
                For RFP submissions, strategic partnerships, pilot scopes, or executive briefings, email our solutions engineering team directly.
              </p>
            </div>
            <a
              href="mailto:contact@simpel.ai"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold py-3.5 px-6 transition-all duration-200"
            >
              <span>contact@simpel.ai</span>
              <ArrowRight className="w-4 h-4 stroke-[2.5]" />
            </a>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="relative z-10 max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 border-t border-white/10 text-center">
          <div className="flex flex-col items-center">
            <ShieldCheck className="w-6 h-6 text-[#00D2EE] mb-2" />
            <span className="text-[14px] font-semibold text-white">Enterprise Guardrails</span>
            <span className="text-[12px] text-[#94A3B8]">Strict data isolation & audit trails</span>
          </div>
          <div className="flex flex-col items-center">
            <Building2 className="w-6 h-6 text-[#00D2EE] mb-2" />
            <span className="text-[14px] font-semibold text-white">Production-Ready</span>
            <span className="text-[12px] text-[#94A3B8]">Proven on Fortune 500 workflows</span>
          </div>
          <div className="flex flex-col items-center">
            <Clock className="w-6 h-6 text-[#00D2EE] mb-2" />
            <span className="text-[14px] font-semibold text-white">Rapid Deployment</span>
            <span className="text-[12px] text-[#94A3B8]">4 to 8 weeks pilot to production</span>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;

